import { h, Fragment } from "preact";
import { useEffect, useState } from "preact/hooks";
import * as helpers from "./helpers";
import ProductInterstitial from "./product-interstitial.jsx";
import ProductCard from "./product-card.jsx";
import storefront from "../storefront";
import * as queries from "../storefront/queries";
import { formatProducts } from "./helpers";
import unwrapNodes from "../unwrapNodes.js";
import InterstitialBannerV2 from "./interstitial-banner-v2.jsx";

const ProductList = ({
  collectionHandle,
  ids,
  perPage = 60,
  sortOption,
  disableLoadMore,
  selectedFilters,
  setTotalProductsCount = () => {},
  setLoading = () => {},
  getSelectedFacets,
  collectionContent,
  preselectColor,
  cardStyle,
}) => {
  const [products, setProducts] = useState([]);
  const [cursor, setCursor] = useState("");
  // Get Products By Collection handle based on Props
  //
  const getProductsByCollection = async (loadMore = false) => {
    let productsQuery = `first: ${perPage}`;
    const facets = getSelectedFacets ? getSelectedFacets(selectedFilters) : "";

    if (loadMore) {
      productsQuery += `${cursor ? "after:" + '"' + cursor + '"' : ""}`;
    }

    if (!loadMore) {
      // ProductCard only works properly on initial render so we need to reset
      // Otherwise previous variant details remain in state.
      // Unfortunately resetting this makes the load more functionality jumpy.
      // Luckily for the loadMore case we know the current products are not changing,
      // so it's safe to skip resetting.
      setProducts([]);
    }

    productsQuery += ", sortKey: " + `${sortOption.value}`;

    if (sortOption.reverse == true) {
      productsQuery += ", reverse: " + `${sortOption.reverse}`;
    }

    if (facets != "") {
      productsQuery += `,filters:[${facets}]`;
    }

    const response = await storefront.request({
      query: queries.productsByCollection(collectionHandle, productsQuery),
    });

    if (response?.data?.collection) {
      let newProducts = [];
      let newCursor = "";

      const banners =
        unwrapNodes(response.data.collection.interstitialBannersV2) || [];

      banners.forEach((banner) => {
        banner.cardWidth = banner.cardWidth ? parseInt(banner.cardWidth) : null;
        banner.displayAfterXCards = banner.displayAfterXCards
          ? parseInt(banner.displayAfterXCards)
          : null;
        banner.overlayOpacity = banner.overlayOpacity
          ? parseFloat(banner.overlayOpacity)
          : null;
        banner.type = "interstitialBannerV2";
      });

      banners.sort((a, b) => a - b);

      if (loadMore) {
        newProducts = formatProducts(products, response);
      } else {
        newProducts = response.data.collection.products.edges.map((product) => {
          return { ...product.node };
        });
      }

      if (response.data.collection.products.pageInfo.hasNextPage) {
        newCursor = response.data.collection.products.edges[perPage - 1].cursor;
      }

      banners.forEach((banner) => {
        const { displayAfterXCards, cardWidth } = banner;

        if (displayAfterXCards > newProducts.length) return;

        const spaces = new Array(cardWidth).fill(null);
        spaces[0] = banner;
        newProducts.splice(displayAfterXCards, 0, ...spaces);
      });

      setProducts(newProducts);
      setCursor(newCursor);
    }

    getCursors(facets);
  };

  const getCursors = async (facets) => {
    let hasNextPage = true;
    let paginationArray = [];

    while (hasNextPage) {
      let productsQuery = "first: " + 250;
      if (facets != "") {
        productsQuery += ",filters:[" + facets + "]";
      }

      const response = await storefront.request({
        query: queries.getCursors(collectionHandle, productsQuery),
      });

      let products = response?.data?.collection?.products;
      if (!products) return;
      await products?.edges?.forEach((edge) => {
        paginationArray.push(edge.cursor);
      });

      if (!products.pageInfo.hasNextPage) {
        hasNextPage = false;
      }
    }

    setTotalProductsCount(paginationArray.length);
  };

  // Get Products By ID Sets based on Props
  const getProductsByIds = async () => {
    if (!ids) return;
    setLoading(true);
    // ProductCard only works properly on initial render so we need to reset
    // Otherwise previous variant details remain in state.
    setProducts([]);
    const response = await storefront.request({
      query: queries.products,
      variables: {
        ids: ids.map((id) => encodeId(id, "Product")),
      },
    });
    if (response?.data?.nodes) {
      const products = response.data.nodes.filter((p) => p);

      setProducts(products);
      setTotalProductsCount(products.length);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (collectionHandle) {
      getProductsByCollection();
    } else {
      getProductsByIds();
    }
  }, [selectedFilters, sortOption, ids]);

  return (
    <Fragment>
      {products.map((item, index) => {
        if (!item) return;
        if (item.type == "interstitialBannerV2")
          return <InterstitialBannerV2 content={item} index={index} />;

        return (
          <ProductCard
            data={item}
            gallery={true}
            index={index}
            preselectColor={preselectColor}
            cardStyle={cardStyle}
          />
        );
      })}

      {products.length > 0 &&
        collectionContent?.map((item) => <ProductInterstitial data={item} />)}

      {cursor && collectionHandle && !disableLoadMore ? (
        <div className="collection__load-more">
          <a
            className="button button--secondary"
            onClick={getProductsByCollection}
          >
            Load More
          </a>
        </div>
      ) : (
        ""
      )}
    </Fragment>
  );
};

export default ProductList;
